import * as React from 'react';
import EDIcon from '../../../../../../../../BaseComponents/Icon/EDIcon';
import EDTooltip from '../../../../../../../../BaseComponents/Tooltip/EDTooltip';

type EDIconType =
  | 'visibility'
  | 'coffin'
  | 'public'
  | 'cremation_plain'
  | 'cremation_act'
  | 'attach_file';

interface IActivity {
  BEVAKNING?: boolean | null;
  KREMERING?: 'KREMERING' | 'JORDBEGRAVNING' | undefined | null;
  SKK_SPRIDNINGSTYP_EXTERN?: boolean | null;
  _typ?: string | null;
  _Associations?: {
    Dokument?: any[] | null;
    Dokument_All?: any[] | null;
    Avlidna?: {
      AKT_MED_URNA?: boolean | null;
    } | null;
  } | null;
}

interface IActivityIconsProps {
  activity: IActivity;
  size?: 'small' | 'medium' | 'large';
  showTooltips?: boolean;
}

interface IIconConfig {
  condition: (activity: IActivity) => boolean;
  icon: EDIconType;
  tooltip: string;
}

const hasDocuments = (activity: IActivity): boolean => {
  return (
    (Array.isArray(activity?._Associations?.Dokument) &&
      activity._Associations.Dokument.length > 0) ||
    (Array.isArray(activity?._Associations?.Dokument_All) &&
      activity._Associations.Dokument_All.length > 0)
  );
};

const isFuneralType = (typ?: string | null): boolean => {
  return typ === 'BEGRAVNING' || typ === 'GRAVSATTNING';
};

export const ActivityIcons: React.FC<IActivityIconsProps> = ({
  activity,
  size = 'small',
  showTooltips = true,
}) => {
  const iconConfigs: IIconConfig[] = [
    {
      condition: (act): boolean => act?.BEVAKNING === true,
      icon: 'visibility' as EDIconType,
      tooltip: 'Bevakas',
    },
    {
      condition: (act): boolean =>
        act?.KREMERING === 'JORDBEGRAVNING' && isFuneralType(act?._typ),
      icon: 'coffin' as EDIconType,
      tooltip: 'Jordbegravning',
    },
    {
      condition: (act): boolean => act?.SKK_SPRIDNINGSTYP_EXTERN === true,
      icon: 'public' as EDIconType,
      tooltip: 'Extern publicering',
    },
    {
      condition: (act): boolean =>
        act?.KREMERING === 'KREMERING' &&
        isFuneralType(act?._typ) &&
        act?._Associations?.Avlidna?.AKT_MED_URNA !== true,
      icon: 'cremation_plain' as EDIconType,
      tooltip: 'Kremering',
    },
    {
      condition: (act): boolean =>
        act?.KREMERING === 'KREMERING' &&
        isFuneralType(act?._typ) &&
        act?._Associations?.Avlidna?.AKT_MED_URNA === true,
      icon: 'cremation_act' as EDIconType,
      tooltip: 'Akt med urna',
    },
    {
      condition: hasDocuments,
      icon: 'attach_file' as EDIconType,
      tooltip: 'Bifogat dokument',
    },
  ];

  const visibleIcons = iconConfigs.filter(config => config.condition(activity));

  const renderIcon = (config: IIconConfig, index: number) => {
    const wrapperStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      ...((config.icon === 'attach_file' && { marginLeft: -3 }) ||
        (config.icon === 'public' && { marginLeft: 1 }) ||
        {}),
    };

    const icon = <EDIcon size={size} iconType={config.icon} />;

    if (!showTooltips) {
      return (
        <span key={`${config.icon}-${index}`} style={wrapperStyle}>
          {icon}
        </span>
      );
    }

    return (
      <EDTooltip
        key={`${config.icon}-${index}`}
        placement="bottom"
        title={config.tooltip}
      >
        <span style={wrapperStyle}>{icon}</span>
      </EDTooltip>
    );
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {visibleIcons.map((config, index) => renderIcon(config, index))}
    </span>
  );
};

export const hasActivityIcons = (activity?: IActivity | null): boolean => {
  if (!activity) return false;

  return (
    activity.BEVAKNING === true ||
    activity.SKK_SPRIDNINGSTYP_EXTERN === true ||
    activity.KREMERING !== undefined ||
    hasDocuments(activity)
  );
};
